import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Layouts
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/layout/ProtectedRoute';
import InstallPrompt from './components/layout/InstallPrompt';

// Auth Pages
import Login from './pages/auth/Login';

// Main Pages
import Dashboard from './pages/Dashboard';
import ParticipantList from './pages/participants/ParticipantList';
import ParticipantForm from './pages/participants/ParticipantForm';
import ImportParticipants from './pages/participants/ImportParticipants';
import CompetitionList from './pages/competitions/CompetitionList';
import CompetitionForm from './pages/competitions/CompetitionForm';
import CompetitionDetails from './pages/competitions/CompetitionDetails';
import CompetitionRegistration from './pages/competitions/CompetitionRegistration';
import MobileResults from './pages/competitions/MobileResults';
import CategoryList from './pages/competitions/CategoryList';
import CategoryForm from './pages/competitions/CategoryForm';
import CategoryDetails from './pages/competitions/CategoryDetails';
import AgeGroupList from './pages/age-groups/AgeGroupList';
import AgeGroupForm from './pages/age-groups/AgeGroupForm';
import ChampionsList from './pages/champions/ChampionsList';
import HouseList from './pages/houses/HouseList';
import HouseForm from './pages/houses/HouseForm';
import HouseDetails from './pages/houses/HouseDetails';
import RefereeList from './pages/referees/RefereeList';
import RefereeForm from './pages/referees/RefereeForm';
import RefereeCardGenerator from './pages/referees/RefereeCardGenerator';
import ImportReferees from './pages/referees/ImportReferees';
import DepartmentList from './pages/departments/DepartmentList';
import DepartmentForm from './pages/departments/DepartmentForm';
import DepartmentDetails from './pages/departments/DepartmentDetails';
import TranslationDashboard from './pages/translations/TranslationDashboard';
import UserRolesPage from './pages/admin/UserRolesPage';
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import SystemLogsPage from './pages/admin/SystemLogsPage';
import ReportsPage from './pages/reports/ReportsPage';
import RefereeAssignmentsReport from './pages/reports/RefereeAssignmentsReport';
import AdminCompetitionList from './pages/competitions/AdminCompetitionList';
import HousePerformanceFullscreen from './pages/houses/HousePerformanceFullscreen';
import ParticipantCardGenerator from './pages/participants/ParticipantCardGenerator';
import PublicRegistrationForm from './pages/public/PublicRegistrationForm';
import StandaloneRegistrationForm from './pages/public/StandaloneRegistrationForm';
import PendingRegistrationsPage from './pages/admin/PendingRegistrationsPage';
import RegistrationSettingsPage from './pages/admin/RegistrationSettingsPage';
import MedalRequirementsPage from './pages/admin/MedalRequirementsPage';
import FamilyConnectionsPage from './pages/participants/FamilyConnectionsPage';
import FamilyDetailPage from './pages/participants/FamilyDetailPage';

function App() {
  const { initialize, initialized } = useAuthStore();

  useEffect(() => {
    if (!initialized) {
      initialize();
    }
  }, [initialize, initialized]);

  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
        <InstallPrompt />
        <Routes>
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          
          {/* Public Routes */}
          <Route path="/houses/performance" element={<HousePerformanceFullscreen />} />
          <Route path="/registrationform" element={<StandaloneRegistrationForm />} />
          <Route path="/register" element={<Navigate to="/registrationform" replace />} />
          
          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<Dashboard />} />
              
              {/* Participants */}
              <Route path="/participants" element={<ParticipantList />} />
              <Route path="/participants/new" element={<ParticipantForm />} />
              <Route path="/participants/:id" element={<ParticipantForm />} />
              <Route path="/participants/:id/edit" element={<ParticipantForm />} />
              <Route path="/participants/cards" element={<ParticipantCardGenerator />} />
              <Route path="/participants/family-connections" element={<FamilyConnectionsPage />} />
              <Route path="/participants/family/:id" element={<FamilyDetailPage />} />
              <Route path="/participants/import" element={<ImportParticipants />} />
              
              {/* Competitions */}
              <Route path="/competitions" element={<CompetitionList />} />
              <Route path="/competitions/new" element={<CompetitionForm />} />
              <Route path="/competitions/:id" element={<CompetitionDetails />} />
              <Route path="/competitions/:id/edit" element={<CompetitionForm />} />
              <Route path="/competitions/:id/register" element={<CompetitionRegistration />} />
              <Route path="/competitions/results" element={<MobileResults />} />
              <Route path="/competitions/admin" element={<AdminCompetitionList />} />
              
              {/* Competition Categories */}
              <Route path="/competitions/categories" element={<CategoryList />} />
              <Route path="/competitions/categories/new" element={<CategoryForm />} />
              <Route path="/competitions/categories/:id" element={<CategoryForm />} />
              <Route path="/competitions/categories/:id/edit" element={<CategoryForm />} />
              <Route path="/competitions/categories/:id/details" element={<CategoryDetails />} />
              
              {/* Age Groups */}
              <Route path="/age-groups" element={<AgeGroupList />} />
              <Route path="/age-groups/new" element={<AgeGroupForm />} />
              <Route path="/age-groups/:id" element={<AgeGroupForm />} />
              <Route path="/age-groups/:id/edit" element={<AgeGroupForm />} />
              
              {/* Champions */}
              <Route path="/champions" element={<ChampionsList />} />

              {/* Houses */}
              <Route path="/houses" element={<HouseList />} />
              <Route path="/houses/new" element={<HouseForm />} />
              <Route path="/houses/:id" element={<HouseForm />} />
              <Route path="/houses/:id/edit" element={<HouseForm />} />
              <Route path="/houses/:id/details" element={<HouseDetails />} />

              {/* Referees */}
              <Route path="/referees" element={<RefereeList />} />
              <Route path="/referees/new" element={<RefereeForm />} />
              <Route path="/referees/:id" element={<RefereeForm />} />
              <Route path="/referees/:id/edit" element={<RefereeForm />} />
              <Route path="/referees/cards" element={<RefereeCardGenerator />} />
              <Route path="/referees/import" element={<ImportReferees />} />

              {/* Departments */}
              <Route path="/departments" element={<DepartmentList />} />
              <Route path="/departments/new" element={<DepartmentForm />} />
              <Route path="/departments/:id" element={<DepartmentForm />} />
              <Route path="/departments/:id/edit" element={<DepartmentForm />} />
              <Route path="/departments/:id/details" element={<DepartmentDetails />} />

              {/* Reports */}
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/reports/referee-assignments" element={<RefereeAssignmentsReport />} />

              {/* Translations */}
              <Route path="/translations" element={<TranslationDashboard />} />
              
              {/* Admin */}
              <Route path="/admin/users" element={<UserRolesPage />} />
              <Route path="/admin/settings" element={<SystemSettingsPage />} />
              <Route path="/admin/logs" element={<SystemLogsPage />} />
              <Route path="/admin/pending-registrations" element={<PendingRegistrationsPage />} />
              <Route path="/admin/registration-settings" element={<RegistrationSettingsPage />} />
              <Route path="/admin/medal-requirements" element={<MedalRequirementsPage />} />
            </Route>
          </Route>
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;